import { useRouter } from 'next/router';
import React, { FC, useEffect, useRef, useState } from 'react';

import { ROUTES } from '@common/constant/routeConstant';
import { ACADEMIC_TYPE } from '@common/type/CourseTypes';
import { useTranslation } from 'next-i18next';
import config from 'src/config';

import PurchaseButtonWithRemark from './purchaseButtonWithRemark';

import AuthorizeModal from '@components/share/modal/authorizeModal/AuthorizeModal';
import AvailableRegisterButton from '@components/templates/bundleDetail/bundleBanner/purchaseButton/availableRegisterButton';
import ConfirmPurchaseBundleDialog from '@components/templates/bundleDetail/bundleBanner/purchaseButton/confirmPurchaseBundleDialog';
import EnrolledButton from '@components/templates/bundleDetail/bundleBanner/purchaseButton/enrolledButton';
import { setBundleEnrollCookie } from '@helpers/auth';
import { createGoogleAnalyticItem, sendPageViewContentCourseDetail } from '@helpers/googleTagManager';
import useBundleDetail from '@hooks/bundle/useBundleDetail';
import useBundleEnrollment from '@hooks/bundle/useBundleEnrollment';
import useCreateUserEnrollFree from '@hooks/enrollment/useCreateUserEnrollFree';
import useDialogWithoutUI from '@hooks/useDialog';
import { useProfile } from '@hooks/useProfile';
import bundleApi from '@redux/bundle/api';
import { BundleEnrollmentStatus, BundleEntity } from '@redux/bundle/type/BundleDetailType';

const { fetchBundleEnrollment } = bundleApi;

type BundleBannerPurchaseButtonProps = {
  isFooter?: boolean;
  isDisableActionIcon?: boolean;
  bundleDetailSeo: BundleEntity;
};
const BundleBannerPurchaseButton: FC<BundleBannerPurchaseButtonProps> = ({
  isFooter = false,
  isDisableActionIcon = false,
  bundleDetailSeo,
}) => {
  const isSendEventGtm = useRef<boolean>(false);
  const { t } = useTranslation('store_course');
  const { bundleEnrollmentStatus, isBundleEnrollmentLoading } = useBundleEnrollment();
  const { idName, id } = bundleDetailSeo;
  const { name, price, bundleDetailData } = useBundleDetail();
  const { profile } = useProfile();
  const router = useRouter();
  const authorizeDialog = useDialogWithoutUI({});
  const confirmPurchaseBundleDialog = useDialogWithoutUI({});
  const { createUserEnrollFree, isLoading, isSuccess } = useCreateUserEnrollFree();
  const [isLoadingFetchEnrollment, setIsLoadingFetchEnrollment] = useState<boolean>(false);

  const isFree = price?.salePrice === 0;

  const handleClickPurchase = async () => {
    if (!profile) {
      authorizeDialog.onOpen();
      return;
    }

    if (isFree) {
      createUserEnrollFree(id);
      return;
    }

    try {
      setIsLoadingFetchEnrollment(true);
      if (isLoadingFetchEnrollment) {
        return;
      }

      const response = await fetchBundleEnrollment({
        bundleIdOrIdName: idName,
      });

      if (
        BundleEnrollmentStatus.ENROLLED_SOME_COURSE === (response?.data?.enrollmentStatus || bundleEnrollmentStatus)
      ) {
        confirmPurchaseBundleDialog.onOpen();
        return;
      }

      router.push(ROUTES.BUNDLE.PAYMENT(idName));
    } finally {
      setIsLoadingFetchEnrollment(false);
    }
  };

  const handleClickViewCourse = () => {
    router.push(ROUTES.USER.MY_COURSE());
  };

  const currentUrl = new URL(router.asPath, config.APP_DOMAIN);

  const onAuthorize = () => {
    setBundleEnrollCookie(currentUrl.pathname);
  };

  const getGa4Items = () => {
    return [
      createGoogleAnalyticItem({
        bundleDetail: bundleDetailData,
        academicType: ACADEMIC_TYPE.REGULAR,
        courseName: name,
      }),
    ];
  };

  useEffect(() => {
    if (!isSuccess) return;

    router.push(ROUTES.USER.MY_COURSE());
  }, [idName, isSuccess, router]);

  useEffect(() => {
    const isAvailable = [
      BundleEnrollmentStatus.AVAILABLE_REGISTER,
      BundleEnrollmentStatus.ENROLLED_SOME_COURSE,
    ].includes(bundleEnrollmentStatus);

    if (!isAvailable || isBundleEnrollmentLoading || isSendEventGtm.current || !bundleDetailData) return;

    isSendEventGtm.current = true;

    sendPageViewContentCourseDetail({
      value: price?.salePrice || 0,
      currency: 'THB',
      items: getGa4Items(),
    });
  }, [isBundleEnrollmentLoading]);

  if (!bundleEnrollmentStatus) return null;

  return (
    <>
      {[BundleEnrollmentStatus.ENROLLED, BundleEnrollmentStatus.ENROLLED_EVERY_COURSE].includes(
        bundleEnrollmentStatus,
      ) ? (
        <EnrolledButton
          bundleDetailSeo={bundleDetailSeo}
          onClick={handleClickViewCourse}
          isFooter={isFooter}
          isDisableActionIcon={isDisableActionIcon}
        />
      ) : null}

      {bundleEnrollmentStatus === BundleEnrollmentStatus.UNPUBLISHED ? (
        <PurchaseButtonWithRemark
          remarkText={t('course_detail.header_banner.purchase.unpublished.button.text')}
          isFooter={isFooter}
          isDisableActionIcon={isDisableActionIcon}
          bundleDetailSeo={bundleDetailSeo}
        />
      ) : null}

      {bundleEnrollmentStatus === BundleEnrollmentStatus.PUBLISHED ? (
        <PurchaseButtonWithRemark
          remarkText={t('course_detail.header_banner.purchase.published.button.text')}
          isFooter={isFooter}
          isDisableActionIcon={isDisableActionIcon}
          bundleDetailSeo={bundleDetailSeo}
        />
      ) : null}

      {[BundleEnrollmentStatus.AVAILABLE_REGISTER, BundleEnrollmentStatus.ENROLLED_SOME_COURSE].includes(
        bundleEnrollmentStatus,
      ) ? (
        <AvailableRegisterButton
          isFooter={isFooter}
          isFree={isFree}
          isDisableActionIcon={isDisableActionIcon}
          isLoading={isLoading || isSuccess}
          onClick={handleClickPurchase}
          bundleDetailSeo={bundleDetailSeo}
        />
      ) : null}

      <AuthorizeModal open={authorizeDialog.isOpened} onClose={authorizeDialog.onClose} onAuthorize={onAuthorize} />

      <ConfirmPurchaseBundleDialog
        isOpen={confirmPurchaseBundleDialog.isOpened}
        onClose={confirmPurchaseBundleDialog.onClose}
        handleConfirm={() => router.push(ROUTES.BUNDLE.PAYMENT(idName))}
      />
    </>
  );
};

export default BundleBannerPurchaseButton;
